import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Button } from "../components/ui/button";
import { 
  ArrowLeft,
  Bell,
  BellRing,
  Smartphone,
  Monitor,
  Check,
  X,
  Loader2,
  TestTube
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { API } from "../App";
import usePushNotifications from "../hooks/usePushNotifications";

const PushSettings = ({ user }) => {
  const navigate = useNavigate();
  const [isTesting, setIsTesting] = useState(false);
  
  const {
    fcmToken,
    permissionStatus,
    isSupported,
    enablePushNotifications,
    disablePushNotifications,
    isEnabled
  } = usePushNotifications();

  // Fetch FCM status from backend
  const { data: fcmStatus, refetch: refetchStatus } = useQuery({
    queryKey: ["fcmStatus"],
    queryFn: async () => {
      const response = await axios.get(`${API}/notifications/fcm/status`);
      return response.data;
    }
  });

  // Fetch push history
  const { data: pushHistory } = useQuery({
    queryKey: ["pushHistory"],
    queryFn: async () => {
      const response = await axios.get(`${API}/notifications/push-history`);
      return response.data;
    }
  });

  const handleEnable = async () => {
    const token = await enablePushNotifications();
    if (token) {
      refetchStatus();
    }
  };

  const handleDisable = async () => {
    await disablePushNotifications();
    refetchStatus();
  };

  const handleTestNotification = async () => {
    setIsTesting(true);
    try {
      const response = await axios.post(`${API}/notifications/fcm/test`);
      if (response.data.status === "no_tokens") {
        toast.error("Enable push notifications first");
      } else {
        toast.success("Test notification sent!");
      }
    } catch (error) {
      toast.error("Failed to send test notification");
    } finally {
      setIsTesting(false);
    }
  };

  const devices = fcmStatus?.devices || [];
  const notifications = pushHistory?.notifications || [];

  return (
    <div className="min-h-screen bg-background pb-24" data-testid="push-settings-page">
      {/* Header */}
      <header className="px-5 pt-4 pb-2 flex items-center gap-4 max-w-lg mx-auto">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate("/settings")}
          className="text-white hover:bg-white/10"
          data-testid="back-btn"
        >
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <h1 className="font-heading font-semibold text-xl text-white">Push Notifications</h1>
      </header>

      <main className="px-5 max-w-lg mx-auto space-y-6">
        {/* Status Card */}
        <div className="glass rounded-2xl p-5">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                isEnabled ? "bg-emerald-500/20" : "bg-white/10"
              }`}>
                {isEnabled ? (
                  <BellRing className="w-6 h-6 text-emerald-400" />
                ) : (
                  <Bell className="w-6 h-6 text-muted-foreground" />
                )}
              </div>
              <div>
                <h2 className="font-medium text-white">Push Notifications</h2>
                <p className="text-xs text-muted-foreground">
                  {isEnabled ? "Enabled" : "Disabled"}
                </p>
              </div>
            </div>
            
            {isEnabled ? (
              <div className="flex items-center gap-2 text-emerald-400">
                <Check className="w-5 h-5" />
                <span className="text-sm">Active</span>
              </div>
            ) : (
              <div className="flex items-center gap-2 text-muted-foreground">
                <X className="w-5 h-5" />
                <span className="text-sm">Off</span>
              </div>
            )}
          </div>

          {!isSupported ? (
            <div className="p-3 bg-amber-500/10 rounded-xl border border-amber-500/20">
              <p className="text-sm text-amber-200">
                Push notifications are not supported in this browser.
              </p>
            </div>
          ) : permissionStatus === "denied" ? (
            <div className="p-3 bg-red-500/10 rounded-xl border border-red-500/20">
              <p className="text-sm text-red-200">
                Notifications blocked. Please enable in browser settings.
              </p>
            </div>
          ) : (
            <Button
              onClick={isEnabled ? handleDisable : handleEnable}
              className={`w-full h-12 ${
                isEnabled 
                  ? "bg-white/10 hover:bg-white/20 text-white" 
                  : "bg-emerald-500 hover:bg-emerald-600"
              }`}
              data-testid="toggle-push-btn"
            >
              {isEnabled ? "Disable Push Notifications" : "Enable Push Notifications"}
            </Button>
          )}
        </div>

        {/* Registered Devices */}
        {devices.length > 0 && (
          <div className="glass rounded-2xl overflow-hidden">
            <div className="p-4 border-b border-white/5">
              <h2 className="font-medium text-white">Registered Devices</h2>
            </div>
            <div className="divide-y divide-white/5">
              {devices.map((device) => (
                <div key={device.token_id} className="p-4 flex items-center gap-4">
                  <div className="w-10 h-10 rounded-xl bg-white/10 flex items-center justify-center">
                    {device.device_type === "web" ? (
                      <Monitor className="w-5 h-5 text-blue-400" />
                    ) : (
                      <Smartphone className="w-5 h-5 text-emerald-400" />
                    )}
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-medium text-white capitalize">
                      {device.device_type} Browser
                    </p>
                    <p className="text-xs text-muted-foreground">
                      Registered {new Date(device.registered_at).toLocaleDateString()}
                    </p>
                  </div>
                  <div className="w-2 h-2 rounded-full bg-emerald-500" />
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Test Notification */}
        {isEnabled && (
          <Button
            onClick={handleTestNotification}
            disabled={isTesting}
            variant="outline"
            className="w-full h-12 border-white/20"
            data-testid="test-push-btn"
          >
            {isTesting ? (
              <Loader2 className="w-5 h-5 mr-2 animate-spin" />
            ) : (
              <TestTube className="w-5 h-5 mr-2" />
            )}
            Send Test Notification
          </Button>
        )}

        {/* Push History */}
        {notifications.length > 0 && (
          <div>
            <h3 className="text-sm font-medium text-white mb-3">Recent Push Notifications</h3>
            <div className="space-y-2">
              {notifications.slice(0, 5).map((notif) => (
                <div
                  key={notif.push_id}
                  className="p-3 rounded-xl bg-white/5 border border-white/10"
                >
                  <div className="flex items-center justify-between mb-1">
                    <p className="text-sm font-medium text-white">{notif.title}</p>
                    <span className={`text-xs px-2 py-0.5 rounded-full ${
                      notif.status === "sent" || notif.status === "mock_sent"
                        ? "bg-emerald-500/20 text-emerald-400"
                        : "bg-amber-500/20 text-amber-400"
                    }`}>
                      {notif.mock ? "mock" : notif.status}
                    </span>
                  </div>
                  <p className="text-xs text-muted-foreground">{notif.body}</p>
                  <p className="text-xs text-muted-foreground mt-1">
                    {new Date(notif.created_at).toLocaleString()}
                  </p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Info */}
        <div className="p-3 rounded-xl bg-blue-500/10 border border-blue-500/20">
          <p className="text-xs text-blue-200 text-center">
            Push notifications work even when the app is closed. You'll receive alerts for transfers, remittances, and important updates.
          </p>
        </div>
      </main>
    </div>
  );
};

export default PushSettings;
